import { useState } from "react";
import { toast } from "sonner";
import { Plus, FileDown } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FormDialog } from "@/components/crm/FormDialog";
import { VENDOR_CATEGORIES } from "@/constants/crm";
import { useFa, useLoad, rp, err, Table, inputCls, download } from "./kit";

const FIELDS = [
  { name: "name", label: "Vendor name", required: true }, { name: "company", label: "Company name" }, { name: "contact", label: "Contact person" }, { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Phone" }, { name: "category", label: "Vendor category", type: "select", options: VENDOR_CATEGORIES }, { name: "gst_no", label: "GSTIN" }, { name: "pan", label: "PAN" },
  { name: "address", label: "Address", type: "textarea" }, { name: "beneficiary_name", label: "Bank beneficiary name" }, { name: "bank_name", label: "Bank name" },
  { name: "account_no", label: "Account number" }, { name: "ifsc", label: "IFSC" },
];

export function FaVendors() {
  const me = useFa();
  const [q, setQ] = useState("");
  const [rows, reload] = useLoad(`/fa/vendors?q=${encodeURIComponent(q)}`);
  const [edit, setEdit] = useState(null);
  const save = async (f) => {
    try {
      const body = Object.fromEntries(FIELDS.map((x) => [x.name, f[x.name] || ""]));
      await (edit.id ? api.put(`/fa/vendors/${edit.id}`, body) : api.post("/fa/vendors", body));
      toast.success("Vendor saved"); reload();
    } catch (x) { err(x); return false; }
  };
  const cols = [{ k: "name", l: "Vendor", r: (v) => <div><div className="font-semibold">{v.company || v.name}</div><div className="text-xs text-zinc-500">{v.contact || v.name} · {v.phone || v.email || ""}</div></div> },
    { k: "category", l: "Category" }];
  if (me.finance) cols.push(
    { k: "bank", l: "Bank", r: (v) => (v.bank_on_file ? `A/c ••${(v.account_no || "").slice(-4)}` : <span className="text-amber-700">Missing</span>) },
    { k: "adv", l: "Advances", right: true, r: (v) => rp(v.stats.advances_paise) }, { k: "bs", l: "Bills submitted", right: true, r: (v) => rp(v.stats.bills_submitted_paise) },
    { k: "ba", l: "Bills approved", right: true, r: (v) => rp(v.stats.bills_approved_paise) }, { k: "pd", l: "Paid", right: true, r: (v) => rp(v.stats.payments_paise) },
    { k: "out", l: "Outstanding", right: true, r: (v) => rp(v.stats.outstanding_paise) }, { k: "un", l: "Unadjusted adv.", right: true, r: (v) => rp(v.stats.unadjusted_paise) },
    { k: "cr", l: "Credit", right: true, r: (v) => rp(v.stats.credit_available_paise) },
    { k: "act", l: "", r: (v) => <div className="flex gap-1">
      <Button size="sm" variant="outline" data-testid={`fa-vendor-edit-${v.id}`} onClick={(e) => { e.stopPropagation(); setEdit(v); }}>Edit</Button>
      <Button size="sm" variant="outline" data-testid={`fa-vendor-statement-${v.id}`} onClick={(e) => { e.stopPropagation(); download(`/fa/statements/vendor/${v.id}`, `vendor-statement-${v.company || v.name}.pdf`); }}><FileDown className="h-4 w-4" /></Button></div> });
  return (
    <div className="space-y-4" data-testid="fa-vendors">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h2 className="text-lg font-extrabold">Vendor management</h2>{!me.finance && <p className="text-xs text-zinc-500">Bank details and balances are visible to Accounts only.</p>}</div>
        <div className="flex gap-2"><input data-testid="fa-vendors-search" placeholder="Search vendors" className={`${inputCls} w-56`} value={q} onChange={(e) => setQ(e.target.value)} />
          {me.finance && <Button data-testid="fa-vendor-new" onClick={() => setEdit({})}><Plus className="mr-1 h-4 w-4" />Add vendor</Button>}</div>
      </div>
      <Table testid="fa-vendors-table" cols={cols} rows={rows || []} />
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit vendor" : "Add vendor"} fields={FIELDS} initial={edit} onSubmit={save} testid="fa-vendor-form" />
    </div>
  );
}
